import classNames from 'classnames';
import * as React from 'react';
import { BackgroundMediaSection } from '../BackgroundMediaSection/BackgroundMediaSection';
import type { BreadcrumbsViewModel } from '~/Features/Breadcrumbs/BreadcrumbsViewModel.csharp';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { Breadcrumbs } from '~/Partials/Breadcrumbs/Breadcrumbs';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { HtmlHeading } from '~/Partials/Controls/HtmlHeading';
import type { BaseImage } from '~/Partials/Picture/Picture';
import styles from './Hero.module.css';
import type { HeroColorVariant } from './HeroColorVariant';
import type { HeroHeadingSize } from './HeroHeadingSize';

interface HeroProps {
    colorVariant: HeroColorVariant;
    heading: HtmlString;
    headingSize: HeroHeadingSize;
    breadcrumbs: BreadcrumbsViewModel;
    description?: HtmlString;
    image?: BaseImage;
    video?: string;
    caption?: string;
    author?: string;
}

export const Hero: React.FC<React.PropsWithChildren<HeroProps>> = props => {
    const {
        colorVariant = 'dark',
        heading,
        headingSize = 'large',
        description,
        image,
        video,
        caption,
        author,
        breadcrumbs,
    } = props;

    const getHeroContent = () => (
        <div className={styles.Content}>
            <Breadcrumbs className={styles.Breadcrumbs} {...breadcrumbs} />
            <div className={styles.Inner}>
                <div className={styles.TextContainer}>
                    <HtmlHeading
                        type="h1"
                        className={classNames(styles.Heading, styles[`Heading--${headingSize}`])}
                        html={heading}
                    />
                    {description && <HtmlDiv html={description} className={styles.Description} />}
                </div>
            </div>
        </div>
    );

    const heroContent = getHeroContent();

    if (video || image) {
        return (
            <BackgroundMediaSection
                image={image}
                video={video}
                caption={caption}
                author={author}
                className={classNames(
                    styles.Hero,
                    styles[`Variant--withMediaBackground`],
                    styles[`Variant--${colorVariant}`]
                )}
                wrapperClassName={styles.Wrapper}
                overlayVariant={colorVariant === 'light' ? 'fullLight' : 'fullDark'}
            >
                {heroContent}
            </BackgroundMediaSection>
        );
    }

    return <div className={classNames(styles.Hero, styles[`Variant--${colorVariant}`])}>{heroContent}</div>;
};
